import { authFetch, buildAgentUrl, throwApiError, unwrapArray } from './core'
import { requestOptions } from './workspace'
import type { FlaggedMessage } from './members'

export interface FlaggedItem {
  id: string
  type: string
  chatId: string
  userId?: string
  chatTitle: string
  classId?: string
  classTitle?: string
  studentUserId?: string
  studentName?: string
  guardian?: 'INPUT' | 'OUTPUT'
  category?: string | null
  severity?: 'ATTENTION' | 'WARNING' | 'RESTRICTED'
  reviewStatus?: 'OPEN' | 'REVIEWED' | 'DISMISSED'
  content: string
  moderationAction: string
  moderationScore: number
  latestExcerpt?: string | null
  flagCount?: number
  lastFlaggedAt?: string
  firstFlaggedAt?: string
  createdAt: string
  reviewed?: boolean
  [key: string]: unknown
}

export interface DashboardStats {
  users: number
  chatSessions: number
  flagged: number
}

export interface CreditPoolStatus {
  totalCredits: number
  usedCredits: number
  remainingCredits: number
  usagePercent: number
  autoResetEnabled: boolean
  resetDay: number
  lastResetAt?: string | null
  updatedAt: string
}

export interface CreditUsageTypeBreakdown {
  type: string
  creditsUsed: number
  activityCount?: number
}

export interface CreditUsageClassBreakdown {
  classId: string
  classTitle: string
  creditsUsed: number
  activityCount: number
  studentCount?: number
}

export interface CreditUsageStudentBreakdown {
  userId: string
  studentName: string
  creditsUsed: number
  activityCount: number
}

export interface CreditUsageTeacherBreakdown {
  userId: string
  teacherName: string
  creditsUsed: number
  activityCount: number
}

export interface CreditUsageDailyBreakdown {
  date: string
  creditsUsed: number
  activityCount?: number
}

export interface CreditUsageResponse {
  period: '7d' | '30d' | 'all'
  totalCreditsUsed: number
  byType: CreditUsageTypeBreakdown[]
  byClass: CreditUsageClassBreakdown[]
  byUser?: CreditUsageStudentBreakdown[]
  byStudent: CreditUsageStudentBreakdown[]
  byTeacher: CreditUsageTeacherBreakdown[]
  trend: CreditUsageDailyBreakdown[]
}

export async function fetchFlaggedList(token: string): Promise<FlaggedItem[]> {
  const response = await authFetch(buildAgentUrl('/admin/flagged'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch flagged list')
  }

  const json = await response.json()
  const flagged = unwrapArray<FlaggedItem>(json, ['flagged', 'data'])
  return flagged.length > 0 || Array.isArray(json) ? flagged : []
}

export async function markFlaggedReviewed(
  token: string,
  flaggedId: string,
): Promise<void> {
  await reviewFlaggedItem(token, flaggedId, { action: 'dismiss', type: 'message' })
}

export async function reviewFlaggedItem(
  token: string,
  flaggedId: string,
  data: { action: 'dismiss' | 'unblock' | 'review'; type?: 'message' | 'conversation'; note?: string },
): Promise<{ reviewed: boolean; action: string; note?: string | null; item?: FlaggedItem }> {
  const response = await authFetch(buildAgentUrl(`/admin/flagged/${flaggedId}/review`), {
    method: 'PUT',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      action: data.action,
      type: data.type ?? 'conversation',
      note: data.note,
    }),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to review flagged item')
  }
  return response.json() as Promise<{ reviewed: boolean; action: string; note?: string | null; item?: FlaggedItem }>
}

export async function fetchDashboardStats(token: string): Promise<DashboardStats> {
  const response = await authFetch(buildAgentUrl('/admin/dashboard'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch dashboard stats')
  }
  return response.json() as Promise<DashboardStats>
}

export async function fetchCreditPool(token: string): Promise<CreditPoolStatus> {
  const response = await authFetch(buildAgentUrl('/admin/credits'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch credit pool')
  }
  return response.json() as Promise<CreditPoolStatus>
}

export async function fetchCreditUsage(
  token: string,
  period: CreditUsageResponse['period'] = '30d',
): Promise<CreditUsageResponse> {
  const url = new URL(buildAgentUrl('/admin/credit-usage'))
  url.searchParams.set('period', period)

  const response = await authFetch(url.toString(), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch credit usage')
  }
  return response.json() as Promise<CreditUsageResponse>
}

export type { FlaggedMessage }
